import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ChevronIcon, DownloadMenuIcon, DeleteMenuIcon, ExcelFileIcon, PdfFileIcon } from "../common/DocumentRowIcons";
import { formatDate, getRealFileName, getUploadUrl } from "src/Common/common";
import {
  can,
  downloadImage,
  downloadPdf,
  handleError,
  isAccountant,
  isClericalOver,
  isManagerOver,
} from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import ConfirmDialog from "src/Common/confirmdialog";
import UploadFilesModal from "../upload/UploadFilesModal";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";

// Upload flow discriminator understood by the /attach backend endpoint.
// 3 = proposal (independent wo_proposal table), 4 = invoice.
const PROPOSAL_UPLOAD_FLOW = 3;

const FILE_ACCEPT =
  "image/*,application/pdf,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const FILE_FOOTER = "Supported formats: JPG, PNG, HEIC, PDF, XLSX";

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const isExcelFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".xlsx");

const isImageFile = (fileName) => !!fileName && /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|tiff?)$/i.test(fileName);

const getDisplayName = (proposal) => proposal.documentName || getRealFileName(proposal.fileName) || proposal.fileName;

const getUploaderName = (proposal) =>
  proposal?.uploadedBy?.name ||
  proposal?.uploadedByCustomerUser?.customer?.companyName ||
  proposal?.uploadedByCustomerUser?.account?.name ||
  "-";

function WoProposals({ order, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(null);

  const canManage = can("wo:addProposal");
  const canDelete = can("wo:deleteProposal");

  const proposals = useMemo(() => order?.proposalFiles || [], [order?.proposalFiles]);

  // Map proposals to the shape AttachmentImageSlider expects. Spreadsheets are
  // excluded — the slider can only render images and PDFs.
  const sliderAttachments = useMemo(
    () =>
      proposals
        .filter((proposal) => !isExcelFile(proposal.fileName))
        .map((proposal) => ({
          id: proposal.id,
          fileName: proposal.fileName,
          isPdf: isPdfFile(proposal.fileName),
          displayName: getDisplayName(proposal),
          description: proposal.description || "",
          tags: proposal.tags || [],
          createdBy: getUploaderName(proposal),
          createdAt: proposal.createdAt,
        })),
    [proposals],
  );

  const handleView = (proposal) => {
    if (!proposal?.fileName) return;
    // No browser preview for spreadsheets — download instead of opening the slider.
    if (isExcelFile(proposal.fileName)) {
      handleDownload(proposal);
      return;
    }
    const index = sliderAttachments.findIndex((attachment) => attachment.id === proposal.id);
    if (index >= 0) setSliderIndex(index);
  };

  const handleDownload = (proposal) => {
    if (!proposal?.fileName) return;
    const downloadName = getDisplayName(proposal);
    if (isPdfFile(proposal.fileName)) {
      downloadPdf(getUploadUrl(proposal.fileName), downloadName);
    } else {
      downloadImage(getUploadUrl(proposal.fileName), downloadName);
    }
  };

  const handleDelete = async () => {
    const proposal = proposalToDelete;
    setProposalToDelete(null);
    if (!proposal?.id || !order?.id) return;
    try {
      await WOService.deleteProposal(order.id, proposal.id, PROPOSAL_UPLOAD_FLOW);
      toast("Proposal deleted successfully");
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div data-tour="wo-sec-proposals" className="w-full bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3]">
      {/* Header */}
      <div
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full px-6 py-4 flex items-center justify-between bg-[#F8F9FE] border-b border-[#E5E8F3] cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            Proposals
          </span>
          <span className="min-w-[24px] px-2 py-[2px] flex items-center justify-center rounded-[16px] bg-[#EEF0F9] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
            {proposals.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {canManage && (
            <button data-tour="wo-upload-proposals"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowUploadModal(true);
              }}
              className="h-[32px] px-3 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white cursor-pointer"
            >
              Upload
            </button>
          )}
          <ChevronIcon expanded={expanded} />
        </div>
      </div>

      <div
        className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="w-full min-h-0 overflow-hidden">
          {proposals.length === 0 ? (
            <div className="w-full flex items-center px-6 h-[64px]">
              <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">No proposals</span>
            </div>
          ) : (
            <div className="w-full flex flex-col">
              {proposals.map((proposal) => {
                const displayName = getDisplayName(proposal);
                return (
                  <div
                    key={proposal.id}
                    className="w-full flex items-center justify-between gap-3 px-6 h-[64px] border-b border-[#E5E8F3] last:border-b-0"
                  >
                    <div
                      className="min-w-0 flex items-center gap-3 cursor-pointer"
                      onClick={() => handleView(proposal)}
                    >
                      <div className="shrink-0 w-[36px] h-[36px] flex items-center justify-center">
                        {isImageFile(proposal.fileName) ? (
                          <img
                            src={getUploadUrl(proposal.fileName)}
                            alt={displayName}
                            className="w-full h-full rounded-[6px] object-cover border border-[#E5E8F3]"
                          />
                        ) : isExcelFile(proposal.fileName) ? (
                          <ExcelFileIcon />
                        ) : (
                          <PdfFileIcon />
                        )}
                      </div>
                      <div className="min-w-0 flex flex-col">
                        <span
                          className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate"
                          title={displayName}
                        >
                          {displayName}
                        </span>
                        <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#5F6477]">
                          {proposal.createdAt ? formatDate(proposal.createdAt, "MMM D, YYYY") : "-"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDownload(proposal)}
                        className="cursor-pointer"
                        title="Download"
                      >
                        <DownloadMenuIcon />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => setProposalToDelete(proposal)}
                          className="cursor-pointer"
                          title="Delete"
                        >
                          <DeleteMenuIcon />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title="DELETE PROPOSAL"
        description="Are you sure you want to delete this proposal?"
        shown={!!proposalToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setProposalToDelete(null)}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderAttachments}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />

      <UploadFilesModal
        shown={showUploadModal}
        order={order}
        uploadFlow={PROPOSAL_UPLOAD_FLOW}
        title="Upload Proposal"
        accept={FILE_ACCEPT}
        footer={FILE_FOOTER}
        toastNoun="proposal"
        onClose={() => setShowUploadModal(false)}
        onUploaded={onRefresh}
      />
    </div>
  );
}

export default WoProposals;
